import { Component, computed, inject, input } from "@angular/core";
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslocoPipe } from "@jsverse/transloco";
import { FormControlHasErrorPipe } from "../../../../common/pipes/form-control-has-error.pipe";
import { PathFromItemPipe } from "../../../../common/pipes/path-from-item.pipe";
import {
    sanitizeDirectusObject,
    sanitizeDirectusRelationObject
} from "../../../../common/utils/sanitize-directus-object";
import { ItemsBronzeSubscriptionFormBlock } from "../../../api/packbo/models/items-bronze-subscription-form-block";
import { ItemsBronzeSubscriptionFormSubmissions } from "../../../api/packbo/models/items-bronze-subscription-form-submissions";
import { BronzeSubscriptionFormBlockService } from "./store/bronze-subscription-form-block.service";
import { BronzeSubscriptionFormBlockStore } from "./store/bronze-subscription-form-block.store";

@Component({
    selector: "packbo-bronze-subscription-form-block",
    templateUrl: "./bronze-subscription-form-block.template.html",
    providers: [BronzeSubscriptionFormBlockService, BronzeSubscriptionFormBlockStore, PathFromItemPipe],
    imports: [TranslocoPipe, ReactiveFormsModule, FormControlHasErrorPipe, FormsModule]
})
export class BronzeSubscriptionFormBlockComponent {
    private readonly bronzeSubscriptionFormBlockStore = inject(BronzeSubscriptionFormBlockStore);
    private readonly bronzeSubscriptionFormBlockService = inject(BronzeSubscriptionFormBlockService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly router = inject(Router);
    private readonly pathFromItemPipe = inject(PathFromItemPipe);

    public readonly bronzeSubscriptionFormBlock = input.required<ItemsBronzeSubscriptionFormBlock>();

    protected readonly isSubmitting = this.bronzeSubscriptionFormBlockStore.submitting;

    protected readonly navigationItems = computed(() =>
        sanitizeDirectusObject(this.bronzeSubscriptionFormBlock()?.navigation_item_ref)
    );
    protected readonly navigationItemPageRef = computed(() =>
        sanitizeDirectusRelationObject(this.bronzeSubscriptionFormBlock()?.navigation_item_ref, "page_ref")
    );

    protected readonly formGroup = this.formBuilder.group({
        firstname: ["", [Validators.required]],
        lastname: ["", [Validators.required]],
        businessName: ["", [Validators.required]],
        email: ["", [Validators.required, Validators.email]],
        message: ["", [Validators.maxLength(1000)]],
        websiteUrl: [""]
    });

    public onBronzeSubscriptionFormSubmit(): void {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            return;
        }

        const submissionForm: ItemsBronzeSubscriptionFormSubmissions = {
            email: this.formGroup.controls.email.value,
            firstname: this.formGroup.controls.firstname.value,
            lastname: this.formGroup.controls.lastname.value,
            business_name: this.formGroup.controls.businessName.value,
            message: this.formGroup.controls.message.value,
            website_url: this.formGroup.controls.websiteUrl.value
        };

        this.bronzeSubscriptionFormBlockService
            .createBronzeSubscriptionFormSubmission(submissionForm)
            .subscribe(async (bronzeSubscriptionFormSubmission) => {
                this.formGroup.reset();
                if (bronzeSubscriptionFormSubmission) {
                    if (this.navigationItemPageRef()) {
                        const confirmationPath = this.pathFromItemPipe.transform(this.navigationItemPageRef(), "page");
                        if (confirmationPath) {
                            await this.router.navigate([confirmationPath]);
                        }
                    }
                }
            });
    }
}
